import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { Box, Grid, Typography } from '@mui/material';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import { TenantPage } from 'layouts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'shared/utils/toast';
import { DocumentCard } from './components/DocumentCard';
import { DocumentSkeleton } from './components/DocumentSkeleton';
import { DocumentsToolbar } from './components/DocumentsToolbar';
import { FolderCard } from './components/FolderCard';
import { IDocument, IFolder } from './types';
import { fmDocumentService } from './api/FMDocumentService';
import { fmFolderService } from './api/FMFolderService';

/**
 * DocumentsList Component
 *
 * An enterprise-grade document management system (DMS) interface that mimics
 * a hierarchical folder and file explorer workflow built natively on Material-UI v5.
 *
 * Core Features:
 * - **Hierarchical Navigation:** Supports deep folder tree traversal with unified Home
 *   and relative Back tracking pipeline executors.
 * - **Parallel Asynchronous Loading:** Leverages `Promise.all` state wrappers to concurrently
 *   fetch folder structures and text assets while isolating asynchronous loading indicators.
 * - **Context-Aware Visual Elements:** Automatically maps operational system extension identifiers
 *   (.pdf, .docx, .png, etc.) to consistent thematic brand color icons.
 * - **Advanced Dual-Mode View:** Seamlessly shifts layouts from hierarchical directory routing
 *   into unified flat matrix query grids when text search filters are actively executing.
 * - **Granular Permission Guards:** Enforces inline role abstractions (`isTechnician`, `isDirector`)
 *   to selectively strip mutation UI workflows (upload modals, deletion alerts) at mount loops.
 *
 * @component
 * @category Components
 * @subcategory Inventory
 *
 * @returns {React.ReactElement} A responsive multi-grid document workspace container wrapped inside a TenantPage frame.
 */
export const DocumentsList = () => {
  const translationPrefix = 'pages.document';
  const { t } = useTranslation();

  const canDelete = useIsTenantAdministrator();

  /* ------------------ State ------------------ */

  const [documents, setDocuments] = useState<IDocument[]>([]);
  const [advancedSearchList, setAdvancedSearchList] = useState<IDocument[]>([]);
  const [folders, setFolders] = useState<IFolder[]>([]);
  const [currentFolder, setCurrentFolder] = useState<IFolder>({
    id: 1,
    name: '/',
    description: '',
    parent: null
  });

  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const [rootFolder, setRootFolder] = useState<IFolder | null>(null);
  const [allFolders, setAllFolders] = useState<IFolder[]>([]);

  /* ------------------ Effects ------------------ */

  useEffect(() => {
    fmFolderService.getAllPaginated().then((res) => {
      const root = res.find((f) => f.parent === null);
      setRootFolder(root || null);
      setCurrentFolder(root || null);
      setAllFolders(res);
    });
  }, []);

  const startingPopolateDocuments = () => {
    if (!currentFolder) return;

    setLoading(true);

    // Documenti con "folder = currentFolder.name"
    Promise.all([
      fmDocumentService.getAllPaginated(0, 0, { folder_exact: currentFolder.name }),
      fmFolderService.getAllPaginated(0, 0, { parent_id: currentFolder.id })
    ])
      .then(([documentsResponse, foldersResponse]) => {
        const documentList = Array.isArray(documentsResponse)
          ? documentsResponse
          : documentsResponse?.results ?? [];
        const folderList = Array.isArray(foldersResponse)
          ? foldersResponse
          : foldersResponse?.results ?? [];
        setDocuments(documentList);
        setFolders(folderList);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    startingPopolateDocuments();
  }, [currentFolder]);

  /* ------------------ Navigation Handlers ------------------ */

  const handleGoBack = () => {
    if (!currentFolder) return;

    if (currentFolder.parent === null) {
      // siamo alla root
      setCurrentFolder(rootFolder);
    } else {
      const parentFolder = allFolders.find((f) => f.id === currentFolder.parent);
      setCurrentFolder(parentFolder || rootFolder);
    }
  };

  const handleDeleteDocument = (documentId: number) => {
    fmDocumentService.delete(documentId).then((res) => {
      if (res.status === 204) {
        toast.success(t('actions.successfully.deleted'));
      } else {
        toast.error(t('actions.successfully.error-generic'));
      }
      startingPopolateDocuments();
    });
  };

  const onSubmitNewDocument = (values) => {
    //upload document to API and handle response
    fmDocumentService
      .uploadDocument(
        values.tipology,
        values.attachment[0],
        values.name,
        values.description || '',
        '',
        '',
        values.category || '',
        values.supplier,
        values.warehouse
      )
      .then((res) => {
        if (res) {
          // if response is successful, show success toast message
          toast.success(t('entities.document.upload-success'));
          startingPopolateDocuments();
        } else {
          // if response is unsuccessful, show error toast message
          toast.error(t('entities.document.upload-error'));
        }
      });
  };

  /**
   * Triggers an asynchronous advanced search query if a valid filter string is provided.
   * Automatically bypasses network requests and flattens result states when inputs are blank.
   */
  const advanceSearch = () => {
    const cleanFilter = filter?.trim();

    if (!cleanFilter) {
      setAdvancedSearchList([]);
      setIsSearching(false);
      return;
    }

    fmDocumentService
      .getAdvancedSearchDocuments(0, 0, { filter: cleanFilter })
      .then((res: IDocument[]) => {
        setIsSearching(true);
        if (res) {
          setAdvancedSearchList(res);
        } else {
          setAdvancedSearchList([]);
        }
      })
      .catch((err: any) => {
        console.error('Advanced search execution failed:', err);
        setAdvancedSearchList([]);
        setIsSearching(false);
      });
  };

  // -------------------------------------------------------- DELETE FOLDER
  const handlerOnDeleteFolder = (folder: IFolder) => {
    if (folder.id === 1) {
      toast.error(t('pages.document.cannot-delete-root-folder'));
      return;
    }

    fmFolderService.delete(folder.id).then((res) => {
      if (res.status === 204) {
        toast.success(t('actions.successfully.deleted'));
        if (currentFolder && currentFolder.id === folder.id) {
          setCurrentFolder(rootFolder);
        } else {
          startingPopolateDocuments();
        }
      } else {
        toast.error(t('actions.successfully.error-generic'));
      }
    });
  };

  const activeDocumentsList = isSearching ? advancedSearchList : documents;

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <DocumentsToolbar
          currentFolder={currentFolder}
          rootFolder={rootFolder}
          allFolders={allFolders}
          filter={filter}
          setCurrentFolder={setCurrentFolder}
          handleGoBack={handleGoBack}
          setFilter={setFilter}
          setAdvancedSearchList={setAdvancedSearchList}
          advanceSearch={advanceSearch}
          onSubmitNewDocument={onSubmitNewDocument}
          setIsSearching={setIsSearching}
        />
        <Box sx={{ width: '100%', mt: 2 }}>
          <Grid container spacing={3}>
            {loading && <DocumentSkeleton />}

            {!loading &&
              !isSearching &&
              advancedSearchList.length === 0 &&
              folders.map((folder) => (
                <FolderCard
                  key={`folder-${folder.id}`}
                  folder={folder}
                  canDelete={canDelete}
                  onSelect={setCurrentFolder}
                  onDelete={handlerOnDeleteFolder}
                />
              ))}

            {!loading &&
              activeDocumentsList.map((doc, i) => (
                <DocumentCard
                  key={`doc-${doc.id || i}`}
                  document={doc}
                  canDelete={canDelete}
                  onDownload={(id) => fmDocumentService.downloadLmAttachment(undefined, id)}
                  onDelete={handleDeleteDocument}
                />
              ))}

            {!loading &&
              ((!isSearching && folders.length === 0 && documents.length === 0) ||
                (isSearching && advancedSearchList.length === 0)) && (
                <Grid item xs={12}>
                  <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
                    <TextSnippetIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {t('global.no-documents-found')}
                    </Typography>
                  </Box>
                </Grid>
              )}
          </Grid>
        </Box>
      </Box>
    </TenantPage>
  );
};

export default DocumentsList;
